/**
 * خروجیِ سفارشیِ فاکتورهای زنجیره تأمین (v2.125).
 *
 * کاربر خودش تعیین می‌کند: بازهٔ تاریخ، طرف‌حساب‌ها، دسته/نوع، وضعیت، ترتیب (تا دو سطح)،
 * گروه‌بندی با جمعِ جزء per طرف‌حساب/ماه، و ستون‌ها (ترتیب + انتخاب). داده را خودش با همین
 * فیلترها از سرور می‌خواند — مستقل از فیلترِ جدولِ صفحه. آخرین تنظیم و «الگو»های نام‌دار
 * per کاربر در `layoutStore` (`pick`) می‌مانند.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Modal, Form, Select, Space, Checkbox, Button, Radio, Input, Divider, Typography, Tag, message, Tooltip,
} from "antd";
import { ArrowUpOutlined, ArrowDownOutlined, SaveOutlined, DeleteOutlined, DownloadOutlined } from "@ant-design/icons";
import { scGetEvents, downloadTableXlsx } from "../../api/client";
import { JalaliDateInput, toJalali, toJalaliDateTime, isoToJalaali } from "../../utils/dateUtils";
import { loadOrder, saveOrder } from "../../utils/layoutStore";
import { eventLabel, statusMeta, STATUS_META, FILTERABLE_EVENT_TYPES } from "./scConstants";
import { enabledCategories, eventTypesForCategory, categoryLabelOfEventType } from "./supplyCategories";
import { faNum } from "../../utils/faNumberFormat";

const { Text } = Typography;
const STORE_ID = "sc-export";
const isImported = (r) => !!r.imported_at || String(r.notes || "").startsWith("ایمپورت از");
const partyOf = (r) => r.source_party_name || r.destination_party_name || "";
const jMonth = (iso) => {
  const j = iso ? isoToJalaali(iso) : null;
  return j ? `${j.jy}/${String(j.jm).padStart(2, "0")}` : "";
};

/** همهٔ ستون‌های قابلِ خروجی. `num` = عددی (جمع‌پذیر در اکسل). */
export const EXPORT_COLUMNS = [
  { key: "event_date", title: "تاریخ فاکتور", get: (r) => toJalali(r.event_date) },
  { key: "category", title: "دسته", get: (r) => categoryLabelOfEventType(r.event_type) },
  { key: "event_type", title: "نوع عملیات", get: (r) => eventLabel(r.event_type) },
  { key: "external_invoice_number", title: "شماره فاکتور", get: (r) => r.external_invoice_number || "" },
  { key: "source_party_name", title: "طرف حساب (مبدأ)", get: (r) => r.source_party_name || "" },
  { key: "destination_party_name", title: "مقصد", get: (r) => r.destination_party_name || "" },
  { key: "item_name", title: "شرح کالا", get: (r) => r.item_name || r.fabric_type || r.yarn_type || "" },
  { key: "fabric_type", title: "نوع پارچه", get: (r) => r.fabric_type || "" },
  { key: "color", title: "رنگ", get: (r) => r.color || "" },
  { key: "order_code", title: "شماره سفارش", get: (r) => r.order_code || "" },
  { key: "external_document_number", title: "شماره سند", get: (r) => r.external_document_number || "" },
  { key: "permit_number", title: "مجوز", get: (r) => r.permit_number || "" },
  { key: "weight_kg", title: "وزن (کیلو)", num: true, get: (r) => r.weight_kg },
  { key: "roll_count", title: "طاقه", num: true, get: (r) => r.roll_count },
  { key: "carton_count", title: "کارتن", num: true, get: (r) => r.carton_count },
  { key: "unit_price", title: "فی (ریال)", num: true, noSum: true, get: (r) => r.unit_price },
  { key: "total_amount", title: "مبلغ (ریال)", num: true, get: (r) => r.total_amount },
  { key: "status", title: "وضعیت", get: (r) => statusMeta(r.status).l },
  { key: "source_kind", title: "منبع", get: (r) => (isImported(r) ? "ایمپورت" : "دستی") },
  { key: "imported_at", title: "تاریخ ایمپورت", get: (r) => (r.imported_at ? toJalaliDateTime(r.imported_at) : "") },
  { key: "import_file", title: "فایلِ ایمپورت", get: (r) => r.import_file || "" },
  { key: "event_code", title: "کد سیستم", get: (r) => r.event_code || "" },
  { key: "notes", title: "توضیحات", get: (r) => r.notes || "" },
];
const COL_BY_KEY = Object.fromEntries(EXPORT_COLUMNS.map((c) => [c.key, c]));
const DEFAULT_COLS = ["event_date", "event_type", "external_invoice_number", "source_party_name",
  "destination_party_name", "item_name", "weight_kg", "unit_price", "total_amount", "status"];

const SORT_FIELDS = [
  { value: "event_date", label: "تاریخ فاکتور" },
  { value: "party", label: "طرف حساب" },
  { value: "destination_party_name", label: "مقصد" },
  { value: "event_type", label: "نوع عملیات" },
  { value: "external_invoice_number", label: "شماره فاکتور" },
  { value: "weight_kg", label: "وزن" },
  { value: "total_amount", label: "مبلغ" },
  { value: "imported_at", label: "تاریخ ایمپورت" },
];
const GROUPS = [
  { value: "none", label: "بدونِ گروه‌بندی" },
  { value: "party", label: "طرف حساب" },
  { value: "destination", label: "مقصد" },
  { value: "month", label: "ماهِ فاکتور" },
  { value: "event_type", label: "نوع عملیات" },
];
const groupKey = (g, r) => ({
  party: () => partyOf(r) || "— بدونِ طرف حساب —",
  destination: () => r.destination_party_name || "— بدونِ مقصد —",
  month: () => jMonth(r.event_date) || "—",
  event_type: () => eventLabel(r.event_type),
}[g] || (() => ""))();

const sortVal = (f, r) => {
  if (f === "party") return partyOf(r);
  if (f === "event_type") return eventLabel(r.event_type);
  if (f === "weight_kg" || f === "total_amount") return r[f] == null ? null : Number(r[f]);
  return r[f] ?? null;
};
function cmp(a, b) {
  if (a == null && b == null) return 0;
  if (a == null) return 1;   // خالی‌ها همیشه آخر
  if (b == null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "fa", { numeric: true });
}

const DEFAULTS = {
  date_from: null, date_to: null, party_ids: [], party_role: "any", category: null, event_types: [],
  statuses: [], sort1: "event_date", dir1: "asc", sort2: "party", dir2: "asc",
  group: "none", subtotals: true, columns: DEFAULT_COLS, title: "",
};

export default function ScExportModal({ open, onClose, parties = [], initial = {} }) {
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);
  const [cols, setCols] = useState(DEFAULT_COLS);
  const [presets, setPresets] = useState(() => loadOrder("pick", `${STORE_ID}:presets`) || []);
  const [presetName, setPresetName] = useState("");
  const [count, setCount] = useState(null);
  const category = Form.useWatch("category", form);
  const group = Form.useWatch("group", form);

  // باز شدن: آخرین تنظیمِ کاربر + فیلترهای فعلیِ صفحه (تاریخ/طرف حساب) روی آن
  useEffect(() => {
    if (!open) return;
    const last = loadOrder("pick", `${STORE_ID}:last`) || {};
    const v = { ...DEFAULTS, ...last };
    if (initial.date_from) v.date_from = initial.date_from;
    if (initial.date_to) v.date_to = initial.date_to;
    if (initial.party_id) v.party_ids = [initial.party_id];
    form.setFieldsValue(v);
    setCols((v.columns || DEFAULT_COLS).filter((k) => COL_BY_KEY[k]));
    setCount(null);
    // eslint-disable-next-line
  }, [open]);

  const typeOptions = useMemo(() => {
    const list = category
      ? FILTERABLE_EVENT_TYPES.filter((e) => eventTypesForCategory(category).includes(e.v))
      : FILTERABLE_EVENT_TYPES;
    return list.map((e) => ({ value: e.v, label: e.l }));
  }, [category]);

  const applyPreset = (p) => {
    form.setFieldsValue({ ...DEFAULTS, ...p.values });
    setCols((p.values.columns || DEFAULT_COLS).filter((k) => COL_BY_KEY[k]));
    message.success(`الگوی «${p.name}» اعمال شد`);
  };
  const savePreset = () => {
    const name = presetName.trim();
    if (!name) { message.warning("برای الگو نام بگذارید"); return; }
    const values = { ...form.getFieldsValue(true), columns: cols };
    // بازهٔ تاریخ جزوِ الگو نیست (هر بار عوض می‌شود)؛ بقیه می‌ماند
    delete values.date_from; delete values.date_to;
    const next = [...presets.filter((p) => p.name !== name), { name, values }];
    setPresets(next); saveOrder("pick", `${STORE_ID}:presets`, next);
    setPresetName("");
    message.success("الگو ذخیره شد");
  };
  const removePreset = (name) => {
    const next = presets.filter((p) => p.name !== name);
    setPresets(next); saveOrder("pick", `${STORE_ID}:presets`, next);
  };

  const moveCol = (k, d) => setCols((cs) => {
    const i = cs.indexOf(k); const j = i + d;
    if (i < 0 || j < 0 || j >= cs.length) return cs;
    const n = [...cs]; [n[i], n[j]] = [n[j], n[i]]; return n;
  });
  const toggleCol = (k, on) => setCols((cs) => (on ? [...cs, k] : cs.filter((x) => x !== k)));

  /** دادهٔ نهایی: خواندن از سرور ← فیلترهای سمتِ کاربر ← ترتیب */
  const fetchRows = async (v) => {
    const params = {};
    if (v.date_from) params.date_from = v.date_from;
    if (v.date_to) params.date_to = v.date_to;
    if (v.party_ids?.length === 1 && v.party_role === "any") params.party_id = v.party_ids[0];
    if (v.event_types?.length === 1) params.event_type = v.event_types[0];
    if (v.statuses?.includes("cancelled")) params.include_cancelled = true;
    let data = (await scGetEvents(params)).data || [];
    const pids = new Set(v.party_ids || []);
    if (pids.size) {
      data = data.filter((r) => (
        v.party_role === "source" ? pids.has(r.source_party_id)
          : v.party_role === "destination" ? pids.has(r.destination_party_id)
            : pids.has(r.source_party_id) || pids.has(r.destination_party_id) || pids.has(r.current_holder_party_id)));
    }
    const types = v.event_types?.length ? v.event_types : (v.category ? eventTypesForCategory(v.category) : null);
    if (types) data = data.filter((r) => types.includes(r.event_type));
    if (v.statuses?.length) data = data.filter((r) => v.statuses.includes(r.status));
    const keys = [[v.sort1, v.dir1], [v.sort2, v.dir2]].filter(([f]) => f);
    // گروه‌بندی = اولین کلیدِ مرتب‌سازی
    if (v.group && v.group !== "none") keys.unshift([`__g`, "asc"]);
    return [...data].sort((a, b) => {
      for (const [f, d] of keys) {
        const c = f === "__g" ? cmp(groupKey(v.group, a), groupKey(v.group, b)) : cmp(sortVal(f, a), sortVal(f, b));
        if (c) return d === "desc" ? -c : c;
      }
      return 0;
    });
  };

  const preview = async () => {
    try { const v = form.getFieldsValue(true); setCount((await fetchRows(v)).length); }
    catch { message.error("خطا در خواندنِ داده"); }
  };

  const run = async () => {
    if (!cols.length) { message.warning("حداقل یک ستون انتخاب کنید"); return; }
    const v = form.getFieldsValue(true);
    setBusy(true);
    try {
      const data = await fetchRows(v);
      if (!data.length) { message.warning("با این فیلترها فاکتوری نیست"); setBusy(false); return; }
      const cdefs = cols.map((k) => COL_BY_KEY[k]);
      const columns = cdefs.map((c) => ({ key: c.key, title: c.title, text: !c.num }));
      const rowOf = (r) => Object.fromEntries(cdefs.map((c) => {
        const x = c.get(r);
        return [c.key, c.num ? (x == null || x === "" ? "" : Number(x)) : (x ?? "")];
      }));
      let rows;
      if (v.group && v.group !== "none") {
        rows = [];
        let cur = null; let bucket = [];
        const flush = () => {
          if (!bucket.length) return;
          if (v.subtotals) {
            // جمعِ جزء متنی است تا جمعِ کلِ پایینِ اکسل دوبار نشمارد
            const sub = { [cdefs[0].key]: `جمعِ «${cur}» — ${faNum(bucket.length)} فاکتور` };
            cdefs.forEach((c, i) => {
              if (i === 0 || !c.num || c.noSum) return;
              const s = bucket.reduce((a, r) => a + (Number(c.get(r)) || 0), 0);
              sub[c.key] = faNum(Math.round(s * 1000) / 1000, { maximumFractionDigits: 3 });
            });
            rows.push(sub, {});
          }
          bucket = [];
        };
        data.forEach((r) => {
          const g = groupKey(v.group, r);
          if (g !== cur) { flush(); cur = g; rows.push({ [cdefs[0].key]: `▸ ${GROUPS.find((x) => x.value === v.group).label}: ${g}` }); }
          bucket.push(r); rows.push(rowOf(r));
        });
        flush();
      } else {
        rows = data.map(rowOf);
      }
      const bits = [];
      if (v.date_from || v.date_to) bits.push(`از ${v.date_from ? toJalali(v.date_from) : "ابتدا"} تا ${v.date_to ? toJalali(v.date_to) : "امروز"}`);
      if (v.party_ids?.length) bits.push("طرف حساب: " + v.party_ids.map((id) => parties.find((p) => p.id === id)?.name).filter(Boolean).join("، "));
      if (v.category) bits.push("دسته: " + (enabledCategories().find((c) => c.key === v.category)?.label || v.category));
      bits.push(`${faNum(data.length)} فاکتور`);
      const title = (v.title || "").trim() || "فاکتورهای زنجیره تأمین";
      await downloadTableXlsx({ title, subtitle: bits.join(" · "), columns, rows, sheet: "فاکتورها" }, `${title}.xlsx`);
      saveOrder("pick", `${STORE_ID}:last`, { ...v, columns: cols });
      message.success(`خروجیِ ${faNum(data.length)} فاکتور دانلود شد`);
      onClose?.();
    } catch (e) {
      message.error(e?.response?.data?.detail || e?.message || "خطا در ساختِ خروجی");
    }
    setBusy(false);
  };

  const partyOptions = parties.map((p) => ({ value: p.id, label: p.name }));

  return (
    <Modal title="خروجیِ سفارشیِ فاکتورها" open={open} onCancel={onClose} width={760}
      footer={[
        <Button key="c" onClick={onClose}>انصراف</Button>,
        <Button key="p" onClick={preview}>{count == null ? "شمارشِ ردیف‌ها" : `${faNum(count)} فاکتور`}</Button>,
        <Button key="x" type="primary" icon={<DownloadOutlined />} loading={busy} onClick={run}
          style={{ background: "#107C41", borderColor: "#107C41" }}>دانلود اکسل</Button>,
      ]}>
      <Form form={form} layout="vertical" style={{ direction: "rtl" }} onValuesChange={() => setCount(null)}>
        {presets.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text type="secondary" style={{ marginInlineEnd: 8 }}>الگوهای من:</Text>
            {presets.map((p) => (
              <Tag key={p.name} color="blue" style={{ cursor: "pointer", marginBottom: 4 }}
                closable closeIcon={<DeleteOutlined />} onClose={(e) => { e.preventDefault(); removePreset(p.name); }}
                onClick={() => applyPreset(p)}>{p.name}</Tag>
            ))}
          </div>
        )}

        <Space wrap align="start" size={12}>
          <Form.Item name="date_from" label="از تاریخ فاکتور"><JalaliDateInput /></Form.Item>
          <Form.Item name="date_to" label="تا تاریخ"><JalaliDateInput /></Form.Item>
          <Form.Item name="title" label="عنوانِ فایل"><Input placeholder="فاکتورهای زنجیره تأمین" style={{ width: 220 }} /></Form.Item>
        </Space>

        <Space wrap align="start" size={12}>
          <Form.Item name="party_ids" label="طرف حساب‌ها" style={{ minWidth: 280 }}>
            <Select mode="multiple" allowClear showSearch optionFilterProp="label" options={partyOptions}
              placeholder="همه" maxTagCount="responsive" style={{ width: 280 }} />
          </Form.Item>
          <Form.Item name="party_role" label="نقشِ طرف حساب">
            <Radio.Group optionType="button" buttonStyle="solid" options={[
              { value: "any", label: "هر نقش" }, { value: "source", label: "مبدأ" }, { value: "destination", label: "مقصد" },
            ]} />
          </Form.Item>
        </Space>

        <Space wrap align="start" size={12}>
          <Form.Item name="category" label="دسته">
            <Select allowClear placeholder="همه" style={{ width: 150 }}
              onChange={() => form.setFieldsValue({ event_types: [] })}
              options={enabledCategories().map((c) => ({ value: c.key, label: c.label }))} />
          </Form.Item>
          <Form.Item name="event_types" label="نوع عملیات">
            <Select mode="multiple" allowClear placeholder="همه" style={{ width: 240 }} options={typeOptions} maxTagCount="responsive" />
          </Form.Item>
          <Form.Item name="statuses" label="وضعیت">
            <Select mode="multiple" allowClear placeholder="همه (بجز لغوشده)" style={{ width: 200 }} maxTagCount="responsive"
              options={Object.entries(STATUS_META).map(([value, m]) => ({ value, label: m.l }))} />
          </Form.Item>
        </Space>

        <Divider orientation="right" plain style={{ margin: "4px 0 12px" }}>ترتیب و گروه‌بندی</Divider>
        <Space wrap align="start" size={12}>
          <Form.Item label="مرتب‌سازیِ اول" style={{ marginBottom: 8 }}>
            <Space.Compact>
              <Form.Item name="sort1" noStyle><Select style={{ width: 150 }} options={SORT_FIELDS} /></Form.Item>
              <Form.Item name="dir1" noStyle><Select style={{ width: 100 }} options={[{ value: "asc", label: "صعودی" }, { value: "desc", label: "نزولی" }]} /></Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item label="مرتب‌سازیِ دوم" style={{ marginBottom: 8 }}>
            <Space.Compact>
              <Form.Item name="sort2" noStyle><Select allowClear placeholder="—" style={{ width: 150 }} options={SORT_FIELDS} /></Form.Item>
              <Form.Item name="dir2" noStyle><Select style={{ width: 100 }} options={[{ value: "asc", label: "صعودی" }, { value: "desc", label: "نزولی" }]} /></Form.Item>
            </Space.Compact>
          </Form.Item>
          <Form.Item name="group" label="گروه‌بندی" style={{ marginBottom: 8 }}>
            <Select style={{ width: 150 }} options={GROUPS} />
          </Form.Item>
          <Form.Item name="subtotals" valuePropName="checked" label="جمعِ جزء" style={{ marginBottom: 8 }}>
            <Checkbox disabled={!group || group === "none"}>جمعِ هر گروه</Checkbox>
          </Form.Item>
        </Space>

        <Divider orientation="right" plain style={{ margin: "4px 0 12px" }}>ستون‌ها (به ترتیبِ خروجی)</Divider>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 260px", border: "1px solid var(--border, #eee)", borderRadius: 8, padding: 8, maxHeight: 260, overflow: "auto" }}>
            {cols.map((k, i) => (
              <div key={k} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 0" }}>
                <Text type="secondary" style={{ width: 20, fontSize: 12 }}>{faNum(i + 1)}</Text>
                <span style={{ flex: 1 }}>{COL_BY_KEY[k].title}</span>
                <Tooltip title="بالاتر"><Button size="small" type="text" icon={<ArrowUpOutlined />} disabled={i === 0} onClick={() => moveCol(k, -1)} /></Tooltip>
                <Tooltip title="پایین‌تر"><Button size="small" type="text" icon={<ArrowDownOutlined />} disabled={i === cols.length - 1} onClick={() => moveCol(k, 1)} /></Tooltip>
                <Button size="small" type="text" danger onClick={() => toggleCol(k, false)}>×</Button>
              </div>
            ))}
            {!cols.length && <Text type="secondary">هیچ ستونی انتخاب نشده</Text>}
          </div>
          <div style={{ flex: "1 1 260px" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>افزودنِ ستون:</Text>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
              {EXPORT_COLUMNS.filter((c) => !cols.includes(c.key)).map((c) => (
                <Tag key={c.key} style={{ cursor: "pointer" }} onClick={() => toggleCol(c.key, true)}>+ {c.title}</Tag>
              ))}
            </div>
            <Button size="small" type="link" style={{ padding: 0, marginTop: 8 }} onClick={() => setCols(DEFAULT_COLS)}>بازگشت به ستون‌های پیش‌فرض</Button>
          </div>
        </div>

        <Divider style={{ margin: "12px 0" }} />
        <Space.Compact style={{ width: "100%", maxWidth: 420 }}>
          <Input placeholder="نامِ الگو (مثلاً «خرید نخ ماهانه به تفکیکِ تأمین‌کننده»)" value={presetName}
            onChange={(e) => setPresetName(e.target.value)} onPressEnter={savePreset} />
          <Button icon={<SaveOutlined />} onClick={savePreset}>ذخیرهٔ الگو</Button>
        </Space.Compact>
      </Form>
    </Modal>
  );
}
